import React, {useContext, useEffect, useRef, useState} from 'react';
import {NavLink, useLocation,} from 'react-router-dom';
import PropTypes from "prop-types";
import ImmutablePropTypes from 'react-immutable-proptypes';
import {connect, useSelector} from 'react-redux'

import {
    Container, Menu, Button, Segment, Icon, Dropdown, Sticky, Label, Image,
    Sidebar, Responsive,
} from "semantic-ui-react";

import {isPresent, canISee} from "../helpers";
import {
  getNavigationItems, isAdmin, getUnreadChats, getAuthUserName, isAuthed,
  getAuth, isMod, getRole, getUnhandledModTasksCount,
} from "../selectors";

import {
    EDIT_NAVIGATION_MODE, EDIT_PAGE_MODE, REGULAR_MODE,
} from "../constants";

import {AdminNavItems} from "./admin";
import {AdminPageMenuItems} from "./admin";
import {Context as ViewModeContext} from "../contexts/ViewMode";

const IncompleteProfileMessage = () => {
    const auth = useSelector(getAuth);
    const location = useLocation();
    // Nothing to say while logged out, or while the member is already on one of
    // the pages that asks for this.
    if(/\/profile\/(edit|init)/.test(location?.pathname) ||
       !isPresent(auth.get('email'))) {
        return null;
    }
    const imageUrl = auth.get('profile_image_url');
    const hasPhoto = isPresent(imageUrl) &&
        !imageUrl.includes("unset_avatar.svg");
    // Only an explicit false nudges. If the flag is missing - a session loaded
    // before the server sent it - stay quiet rather than tell someone who wrote
    // a description that they did not.
    const hasDescription = auth.get('has_description') !== false;
    // Missing name or place still leads into the setup wizard, unchanged.
    if(!auth.get('nickname') || !auth.get('locatable')) {
        return <div className="incomplete-profile-message" data-cy="incomplete-profile">
            <NavLink to="/profile/init">
                Bitte drück hier, um dein Profil zu vervollständigen.
            </NavLink>
        </div>
    }
    if(hasPhoto && hasDescription) {
        return null;
    }
    // Written out per case rather than assembled: German needs fehlt/fehlen to
    // agree, and "ein paar Zeilen" is plural even on its own.
    const missing = !hasPhoto && !hasDescription ?
        "Dir fehlen noch ein Foto und ein paar Zeilen über dich." :
        (!hasPhoto ?
            "Dir fehlt noch ein Foto." :
            "Dir fehlen noch ein paar Zeilen über dich.");
    // The nudge asks for something, so it also says what the member gets back.
    const reason = !hasPhoto ?
        "Mit Foto wirst du viel häufiger gefunden und angeschrieben." :
        "Wer etwas über dich lesen kann, schreibt dich eher an.";
    return <div className="incomplete-profile-message" data-cy="incomplete-profile">
        <NavLink to="/profile/edit">
            <strong>{missing}</strong>
            {" "}{reason}
            {" "}Jetzt ergänzen.
        </NavLink>
    </div>
};

const ChatMenuItem = () => {
    const chatUnreadCount = useSelector(getUnreadChats);
    // The link shows only an icon, so screen readers need its name spelled out.
    // The count is conversations with unread messages, not messages.
    const label = chatUnreadCount > 0 ?
        `Nachrichten, ${chatUnreadCount} ${chatUnreadCount === 1 ?
            "ungelesener Chat" : "ungelesene Chats"}` :
        "Nachrichten";
    return (
        <Menu.Item header as={NavLink} to="/chat" tabIndex="0"
            aria-label={label}
        >
            <Icon name="chat"/>
            {chatUnreadCount > 0 ?
                <Label color="red" floating className="notification-count">
                    {chatUnreadCount}
                </Label> : null
            }
        </Menu.Item>
    );
}

const NavItems = () => {
    const allNavItems = useSelector(getNavigationItems);
    const myrole = useSelector(getRole);
    const navItems = allNavItems.filter(
        m => m.get('active') && m.get('location') === 'top' &&
        canISee(myrole, m.get('visibility')));
    return navItems.map(m => (m.get('path').indexOf('home') >= 0) ? (
        <Menu.Item header name="brand" as={NavLink} to="/" exact
            key={m.get('slug')} tabIndex="0"
        >
            <Image src="/images/logo_header.svg" alt="Kuscheldate"/>
        </Menu.Item>
    ) : (
        <Menu.Item name={m.get('title')} key={m.get('slug')}
            as={NavLink} to={m.get('path')} exact
            tabIndex="0"
        >
            {m.get('title')}
        </Menu.Item>
    ));
};

const UserMenu = () => {
    const admin = useSelector(isAdmin);
    const mod = useSelector(isMod);
    const authed = useSelector(isAuthed);
    const auth = useSelector(getAuth);
    const modTasks = useSelector(getUnhandledModTasksCount);
    const hasImportant = modTasks > 0;
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);

    // A disclosure (button + links) instead of semantic's <Dropdown>: that one
    // is a value picker - announced as a "listbox" of "options", its keyboard
    // handling only works with an options list, and it closed as soon as Tab
    // reached one of its own links, so keyboard users never got to
    // Einstellungen, Abonnement or Ausloggen. Same classes and nesting as the
    // Dropdown rendered, so the existing styles still apply.
    useEffect(() => setOpen(false), [location.pathname]);
    useEffect(() => {
        if (!open) return;
        const onOutside = (ev) => {
            if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [open]);

    const menuLinks = () => [...rootRef.current.querySelectorAll('.menu a.item')];
    const onKeyDown = (ev) => {
        if (ev.key === 'Escape' && open) {
            ev.preventDefault();
            setOpen(false);
            triggerRef.current.focus();
        } else if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
            ev.preventDefault();
            if (!open) {
                setOpen(true);
                // The menu is display:none until the re-render.
                setTimeout(() => menuLinks()[0]?.focus(), 0);
                return;
            }
            const links = menuLinks();
            const at = links.indexOf(document.activeElement);
            const next = ev.key === 'ArrowDown' ?
                (at + 1) % links.length : (at <= 0 ? links.length - 1 : at - 1);
            links[next]?.focus();
        }
    };
    const onBlur = (ev) => {
        if (open && !rootRef.current.contains(ev.relatedTarget)) setOpen(false);
    };
    const greetingLabel = hasImportant ?
        `Benutzermenü, ${modTasks} offene Moderationsaufgabe${modTasks > 1 ? "n" : ""}` :
        "Benutzermenü";

    return (
        <div ref={rootRef}
            className={`ui item dropdown user-menu${open ? " active visible" : ""}`}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
        >
            <button type="button" className="user-menu-trigger" ref={triggerRef}
                aria-label={greetingLabel}
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            >
                <span data-cy="usermenu">
                    <Icon.Group>
                        <Icon name="user" />
                        {hasImportant ? <Icon name="exclamation circle" color="red"
                            className="user-header-notification"
                        /> : null}
                    </Icon.Group><span className="mobile hidden" data-cy="authname">
                        {auth.get('nickname')}
                    </span>
                </span>
            </button>
            <i aria-hidden="true" className="dropdown icon"
                onClick={() => setOpen(o => !o)}
            />
            <div className={`menu transition${open ? " visible" : ""}`}
                onClick={(ev) => ev.target.closest('a.item') && setOpen(false)}
            >
                <div className="item mobile only name disabled">
                    Hallo {auth.get('nickname')}
                </div>
                <NavLink className="item" to="/profile">
                    Profil
                </NavLink>
                <NavLink className="item" to="/settings">
                    Einstellungen
                </NavLink>
                {(admin || mod) ? <div className="divider" /> : null}
                {admin ? <NavLink className="item" to="/admin/">
                    Administration
                </NavLink> : null}
                {mod ? <NavLink className="item" to="/mods/">
                    Moderation
                    {modTasks > 0 ? <Label color="red" corner="right" size="mini">
                        {modTasks}
                    </Label> : null}
                </NavLink> : null}
                {authed ? (
                    <NavLink className="item" to="/subscriptions">
                        Abonnement
                    </NavLink>
                ) : null}
                <div className="divider" />
                <NavLink className="item" to="/logout" data-cy="logout">
                    Ausloggen
                </NavLink>
            </div>
        </div>
    );
};

const AuthButtons = () => {
    return (
        <Menu.Item position="right" name="account">
            <Menu secondary>
                <Menu.Item as={NavLink} to="/login" tabIndex="0">
                    <Icon name="user outline" /> Anmelden
                </Menu.Item>
            </Menu>
        </Menu.Item>
    );
};

const AccountItem = () => {
    const authed = useSelector(isAuthed);
    const auth = useSelector(getAuth);
    if(authed) {
       if (auth.get('profile_image_url') &&
           !auth.get('profile_image_url').includes("unset_avatar.svg")) {
                return (
                    <Menu.Item position="right" name="account">
                        <Menu className="right" secondary>
                            <ChatMenuItem />
                            <UserMenu />
                        </Menu>
                    </Menu.Item>
                );
        } else {
            return (
                <Menu.Item position="right" name="account">
                    <Menu className="right" secondary>
                        <UserMenu />
                    </Menu>
                </Menu.Item>
            );
        }
    } else {
        return <AuthButtons />
    }
};

const Header = ({stickyRef, onSidebarToggle}) => {
    const {mode} = useContext(ViewModeContext);
    return (
        <Sticky context={stickyRef} className="header-sticky">
            <Segment className="cuddle" inverted as="header">
                <div className="header_wrapper">
                    <Container>
                        <Menu size="large" className="cuddle"
                            inverted borderless
                        >
                            <Menu.Item className={(mode === EDIT_PAGE_MODE) ?
                                "page-edit-menu-wrap" : undefined}
                            >
                                <Menu secondary
                                    className={(mode === EDIT_PAGE_MODE) ?
                                        "page-edit-menu" : undefined}
                                >
                                    <Responsive maxWidth={Responsive.onlyTablet.maxWidth} as={React.Fragment}>
                                        <Menu.Item as="button" onClick={onSidebarToggle}
                                            aria-label="Menü"
                                        >
                                            <Icon name="sidebar" />
                                        </Menu.Item>
                                        {/* The editor these controls belong to is not gated by
                                            width, so the controls must not be either: without
                                            Speichern and Abbrechen there is no way to save the
                                            blocks below, and no way out of edit mode at all -
                                            the account menu is hidden outside REGULAR_MODE. */}
                                        {(mode === EDIT_PAGE_MODE) ?
                                            <AdminPageMenuItems /> : null}
                                    </Responsive>
                                    <Responsive {...Responsive.onlyComputer} as={React.Fragment}>
                                        {(mode === EDIT_NAVIGATION_MODE) ?
                                            <AdminNavItems location="top"/> :
                                            (
                                                (mode === EDIT_PAGE_MODE) ?
                                                    <AdminPageMenuItems /> :
                                                    <NavItems/>
                                            )
                                        }

                                    </Responsive>
                                </Menu>
                            </Menu.Item>
                            {(mode === REGULAR_MODE) ? <AccountItem /> : null}
                        </Menu>
                        {(mode === REGULAR_MODE) ? <IncompleteProfileMessage /> : null}
                    </Container>
                </div>
            </Segment>
        </Sticky>
    );
}

const MobileSidebar = ({visible}) => {
    return (
        <Sidebar
            as={Menu}
            animation="overlay"
            icon="labeled"
            inverted
            vertical
            visible={visible}
            className="page-sidebar"
        >
            <NavItems />
        </Sidebar>
    );
}

Header.Sidebar = MobileSidebar;

export default Header;
